/**
 * W-052 — Transactions list. Stories: US-A12.
 *
 * AC1 newest first with user, plan, amount, source, status, date
 * AC2 filters by status, source and date range
 * AC4 CSV export of the current filtered view
 *
 * The ledger is append-only and written only by edge functions (docs/03 §4.3),
 * so this screen is strictly read-only — there is no create, edit or delete.
 */
import { List, useTable } from '@refinedev/antd'
import type { CrudFilters } from '@refinedev/core'
import { CloseOutlined, FilterOutlined } from '@ant-design/icons'
import { Button, DatePicker, Select, Space, Table, Tag, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link } from 'react-router'
import { ModelInsights, StatusTag } from '@/components'
import { downloadCsv } from '@/lib/csv'
import { formatPrice } from '@horizon/i18n'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { Transaction, TransactionStatus } from '@/types'
import { SUBSCRIPTION_SOURCES, TRANSACTION_STATUSES } from '@/types'

interface TransactionRow extends Transaction {
  /** Joined for display: PostgREST embeds the related rows. */
  profiles?: { full_name: string } | null
  plans?: { name_fr: string } | null
}

export const TransactionListPage = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const [status, setStatus] = useState<TransactionStatus | undefined>()
  const [provider, setProvider] = useState<string | undefined>()
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)

  const { tableProps, setFilters } = useTable<TransactionRow>({
    resource: 'transactions',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 50 },
    meta: { select: '*, profiles(full_name), plans(name_fr)' },
    syncWithLocation: true,
  })

  const apply = () => {
    const filters: CrudFilters = []
    if (status) filters.push({ field: 'status', operator: 'eq', value: status })
    if (provider) filters.push({ field: 'provider', operator: 'eq', value: provider })
    if (range) {
      filters.push({
        field: 'created_at',
        operator: 'gte',
        value: range[0].startOf('day').toISOString(),
      })
      filters.push({
        field: 'created_at',
        operator: 'lte',
        value: range[1].endOf('day').toISOString(),
      })
    }
    setFilters(filters, 'replace')
  }

  const rows = (tableProps.dataSource ?? []) as TransactionRow[]
  const resultCount =
    (typeof tableProps.pagination === 'object' ? tableProps.pagination.total : undefined) ??
    rows.length
  const activeFilters = [
    status
      ? translate('admin.screens.transactions.filterStatus', {
          value: translate(`admin.status.transaction.${status}`),
        })
      : null,
    provider ? translate('admin.screens.transactions.filterSource', { value: provider }) : null,
    range
      ? translate('admin.screens.transactions.filterRange', {
          from: range[0].format('DD/MM/YYYY'),
          to: range[1].format('DD/MM/YYYY'),
        })
      : null,
  ].filter((filter): filter is string => Boolean(filter))
  const reset = () => {
    setStatus(undefined)
    setProvider(undefined)
    setRange(null)
    setFilters([], 'replace')
  }

  return (
    <List
      title={translate('admin.pages.transactions')}
      canCreate={false}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.transactions.subtitle'),
      }}
      headerButtons={
        <Button
          onClick={() =>
            downloadCsv(translate('admin.screens.transactions.csvFileName'), rows, [
              {
                header: translate('admin.columns.date'),
                value: row => dayjs(row.created_at).format('YYYY-MM-DD HH:mm'),
              },
              { header: translate('admin.columns.user'), value: row => row.profiles?.full_name },
              { header: translate('admin.common.plan'), value: row => row.plans?.name_fr },
              {
                header: translate('admin.screens.transactions.amountCsvHeader'),
                value: row => row.amount_mru,
              },
              { header: translate('admin.columns.source'), value: row => row.provider },
              { header: translate('admin.columns.reference'), value: row => row.provider_ref },
              { header: translate('admin.columns.status'), value: row => row.status },
            ])
          }
        >
          {translate('admin.common.exportCsv')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="transactions" />
        <div className="admin-filter-bar">
          <Space wrap>
            <Select
              allowClear
              placeholder={translate('admin.columns.status')}
              style={{ width: 180 }}
              value={status}
              onChange={value => setStatus(value)}
              options={TRANSACTION_STATUSES.map(value => ({
                value,
                label: translate(`admin.status.transaction.${value}`),
              }))}
            />
            <Select
              allowClear
              placeholder={translate('admin.columns.source')}
              style={{ width: 180 }}
              value={provider}
              onChange={value => setProvider(value)}
              options={SUBSCRIPTION_SOURCES.map(value => ({ value, label: value }))}
            />
            <DatePicker.RangePicker
              format="DD/MM/YYYY"
              value={range}
              onChange={value =>
                setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
              }
            />
            <Button type="primary" icon={<FilterOutlined />} onClick={apply}>
              {translate('admin.common.apply')}
            </Button>
            <Button icon={<CloseOutlined />} disabled={!activeFilters.length} onClick={reset}>
              {translate('admin.common.reset')}
            </Button>
          </Space>
        </div>

        <div className="admin-filter-summary">
          <Space wrap size={[6, 6]}>
            {activeFilters.length ? (
              activeFilters.map(filter => <Tag key={filter}>{filter}</Tag>)
            ) : (
              <Typography.Text type="secondary">
                {translate('admin.screens.transactions.all')}
              </Typography.Text>
            )}
          </Space>
          <Typography.Text type="secondary">
            {translate('admin.common.resultCount', { count: resultCount })}
          </Typography.Text>
        </div>

        <Table<TransactionRow>
          {...tableProps}
          rowKey="id"
          scroll={{ x: 920 }}
          sticky
          columns={[
            {
              title: translate('admin.columns.date'),
              dataIndex: 'created_at',
              render: (value: string, row: TransactionRow) => (
                <Link to={`/transactions/show/${row.id}`}>
                  {dayjs(value).format('DD/MM/YYYY HH:mm')}
                </Link>
              ),
            },
            {
              title: translate('admin.columns.user'),
              dataIndex: ['profiles', 'full_name'],
              render: (value: string | undefined) => value ?? '—',
            },
            {
              title: translate('admin.common.plan'),
              dataIndex: ['plans', 'name_fr'],
              render: (value: string | undefined) => value ?? '—',
            },
            {
              title: translate('admin.columns.amount'),
              dataIndex: 'amount_mru',
              align: 'end',
              render: (value: number) => (
                <Typography.Text strong>{formatPrice(value, locale)}</Typography.Text>
              ),
            },
            { title: translate('admin.columns.source'), dataIndex: 'provider' },
            {
              title: translate('admin.columns.reference'),
              dataIndex: 'provider_ref',
              render: (value: string | null) =>
                value ? <Typography.Text code>{value}</Typography.Text> : '—',
            },
            {
              title: translate('admin.columns.status'),
              dataIndex: 'status',
              render: (value: TransactionStatus) => <StatusTag kind="transaction" value={value} />,
            },
          ]}
        />
      </Space>
    </List>
  )
}
